function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import cn from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import forEach from 'lodash/forEach';
import forOwn from 'lodash/forOwn';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import slice from 'lodash/slice';
import sortBy from 'lodash/sortBy';
import startsWith from 'lodash/startsWith';
import toLower from 'lodash/toLower';
import { ProfileContext } from 'context';
import { formatDateAsObject, formatTimestamp } from 'helpers/datetime';
import { useAction } from 'hooks';
import Avatar from 'components/Avatar';
import CheckboxRadio from 'components/Form/CheckboxRadio';
import Pagination from 'components/Pagination';
import SyncIcon from 'svg/sync.svg';
import App from 'modules/App';
import DataSources from 'modules/DataSources';
import * as actions from '../../actions';
import * as selectors from '../../selectors';
import ColHeaders from './ColHeaders';
import styles from './ImportsHistory.pcss';
const ImportsHistory = ()=>{
    useStyles(styles);
    const profileContext = useContext(ProfileContext);
    const phiSet = useSelector(profileContext.selectors.phiSet);
    const fromImports = useSelector(selectors.fromImports) || [];
    const devices = useSelector(App.selectors.devices);
    const caseTypes = useSelector(App.selectors.caseTypes);
    const localizationResources = useSelector(App.selectors.localizationResources);
    const dataSources = useSelector(DataSources.selectors.dataSources);
    const changeFromImports = useAction(actions.changeFromImports);
    const imports = get(phiSet, 'imports');
    const [page, setPage] = useState(0);
    useEffect(()=>{
        setPage(0);
    }, [
        imports
    ]);
    // useEffect(() => {
    //   ReactTooltip.rebuild();
    // });
    const { importDocuments, matchedDevices, totalPages } = useMemo(()=>{
        if (!imports) {
            return {
                importDocuments: [],
                matchedDevices: {},
                totalPages: 1
            };
        }
        const matchedDevicesMap = {};
        const setDevice = (serialNumberToken)=>{
            const foundDevices = filter(devices, (device)=>startsWith(serialNumberToken, device.sku));
            matchedDevicesMap[serialNumberToken] = reduce(foundDevices, (acc, matchedDevice)=>acc && acc.sku.length > matchedDevice.sku.length ? acc : matchedDevice, null);
        };
        const flattenImportDocuments = [];
        forOwn(imports, (deviceImports, serialNumberToken)=>{
            const { deviceName, documents } = deviceImports;
            setDevice(serialNumberToken);
            forEach(documents, (document)=>{
                flattenImportDocuments.push(_object_spread_props(_object_spread({}, document), {
                    deviceName,
                    serialNumberToken
                }));
            });
        });
        const sortedImportDocuments = sortBy(flattenImportDocuments, (d)=>-d.timestamp);
        const calculatedTotalPages = Math.ceil(sortedImportDocuments.length / 10);
        return {
            importDocuments: sortedImportDocuments,
            matchedDevices: matchedDevicesMap,
            totalPages: calculatedTotalPages
        };
    }, [
        imports,
        devices
    ]);
    const onChangePage = (newPage)=>{
        setPage(newPage);
    };
    const onChangeFromImports = (importDocumentId)=>{
        changeFromImports(importDocumentId, imports);
    };
    const importDocumentsPage = useMemo(()=>{
        const perPage = 10;
        const start = page * 10;
        const end = start + perPage;
        return slice(importDocuments, start, end);
    }, [
        page,
        importDocuments
    ]);
    const importDocumentsByMonths = useMemo(()=>{
        const monthlyImportDocuments = {
            index: [],
            months: {}
        };
        forEach(importDocumentsPage, (importDocument, idx)=>{
            const dateObj = formatDateAsObject(importDocument.timestamp * 1000); // Date needs ms
            if (!dateObj) {
                return;
            }
            const { month, year } = dateObj;
            const key = `${year}-${month}`;
            if (!monthlyImportDocuments.months[key]) {
                monthlyImportDocuments.index.push(key);
                monthlyImportDocuments.months[key] = {
                    importDocuments: [],
                    month: moment().month(month - 1).format('MMMM'),
                    year
                };
            }
            monthlyImportDocuments.months[key].importDocuments.push(_object_spread_props(_object_spread({}, importDocument), {
                idx
            }));
        });
        return monthlyImportDocuments;
    }, [
        importDocumentsPage
    ]);
    const renderSourceFigure = (importDocument)=>{
        const { serialNumberToken, deviceName } = importDocument;
        if (serialNumberToken === 'sync') {
            return /*#__PURE__*/ _jsx("figure", {
                className: styles.deviceFigure,
                "data-for": "importsHistoryTooltip",
                "data-tip": "Sync",
                children: /*#__PURE__*/ _jsx(SyncIcon, {
                    className: styles.deviceFigure__icon
                })
            });
        }
        if (serialNumberToken === 'glucocontrodesktop') {
            return /*#__PURE__*/ _jsx("figure", {
                className: styles.deviceFigure,
                "data-for": "importsHistoryTooltip",
                "data-tip": "GlucoControDesktop",
                children: /*#__PURE__*/ _jsx(SyncIcon, {
                    className: styles.deviceFigure__icon
                })
            });
        }
        if (serialNumberToken === 'snaq') {
            return /*#__PURE__*/ _jsx("figure", {
                className: styles.deviceFigure,
                "data-for": "importsHistoryTooltip",
                "data-tip": "Snaq",
                children: /*#__PURE__*/ _jsx("img", {
                    src: "/assets/svg/snaq.svg",
                    alt: "snaq.io"
                })
            });
        }
        const device = get(matchedDevices, serialNumberToken);
        if (device) {
            const caseTypeId = get(device, 'caseTypeId');
            const caseType = find(caseTypes, {
                caseTypeId
            });
            const icon = get(caseType, 'icon', null);
            return /*#__PURE__*/ _jsx("figure", {
                className: styles.deviceFigure,
                "data-for": "importsHistoryTooltip",
                "data-tip": device.name,
                dangerouslySetInnerHTML: {
                    __html: icon
                }
            });
        }
        const dataSource = find(dataSources, (ds)=>toLower(ds.dataSourceProvider) === serialNumberToken);
        if (dataSource) {
            const dataSourceProvider = get(localizationResources, [
                dataSource.nameKey,
                'value'
            ], dataSource.dataSourceProvider);
            return /*#__PURE__*/ _jsx("figure", {
                className: styles.dataSource,
                "data-for": "importsHistoryTooltip",
                "data-tip": dataSourceProvider,
                children: /*#__PURE__*/ _jsx(Avatar, {
                    avatarImg: dataSource.logo,
                    name: dataSourceProvider,
                    className: styles.dataSource__logo,
                    imgClassName: styles.dataSource__logo__img,
                    initialsClassName: styles.dataSource__logo__initials
                })
            });
        }
        return /*#__PURE__*/ _jsx("figure", {
            className: styles.deviceFigure,
            "data-for": "importsHistoryTooltip",
            "data-tip": deviceName,
            children: /*#__PURE__*/ _jsx("img", {
                src: "/assets/svg/generic-glucometer.svg",
                alt: deviceName
            })
        });
    };
    const renderImport = (importDocument)=>{
        const { idx, importDocumentId, timestamp, maxResultDate, minResultDate, maxTimeSeriesResourcesDate, minTimeSeriesResourcesDate, readingsCount, timeSeriesResourcesCount } = importDocument;
        return /*#__PURE__*/ _jsxs("div", {
            className: cn(styles.table__row, {
                [styles.odd]: !(idx % 2)
            }),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.table__col, 'text--bold'),
                    children: formatTimestamp(timestamp, 'L, LT')
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.table__col,
                    children: [
                        formatTimestamp(minResultDate || minTimeSeriesResourcesDate, 'L'),
                        ' - ',
                        formatTimestamp(maxResultDate || maxTimeSeriesResourcesDate, 'L')
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.table__col, styles['table__col--small']),
                    children: /*#__PURE__*/ _jsx("span", {
                        className: styles.readingsCount,
                        children: readingsCount || timeSeriesResourcesCount
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.table__col, styles['table__col--small']),
                    children: renderSourceFigure(importDocument)
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.table__col, styles['table__col--small']),
                    children: /*#__PURE__*/ _jsx(CheckboxRadio, {
                        id: importDocumentId,
                        inputValue: "1",
                        value: includes(fromImports, importDocumentId) ? '1' : '',
                        onChange: ()=>onChangeFromImports(importDocumentId)
                    })
                })
            ]
        }, importDocumentId);
    };
    const renderMonth = (monthId, months)=>{
        const month = get(months, monthId);
        if (!month) {
            return null;
        }
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.month,
            children: [
                /*#__PURE__*/ _jsxs("h3", {
                    className: styles.month__name,
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: month.month
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "mt-2",
                            children: month.year
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.month__imports,
                    children: map(month.importDocuments, (importDocument)=>renderImport(importDocument))
                })
            ]
        }, monthId);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        children: [
            /*#__PURE__*/ _jsx(ColHeaders, {}),
            map(importDocumentsByMonths.index, (monthId)=>renderMonth(monthId, importDocumentsByMonths.months)),
            /*#__PURE__*/ _jsx(Pagination, {
                perPage: 10,
                onPageChange: onChangePage,
                page: page,
                totalPages: totalPages,
                isPerPageOff: true
            }),
            /*#__PURE__*/ _jsx(ReactTooltip, {
                id: "importsHistoryTooltip",
                place: "top",
                type: "dark",
                effect: "solid"
            })
        ]
    });
};
export default ImportsHistory;
